import styles from "./SideNavigationProfile.module.scss";
import { SideNavigationProfileProps } from "../types/SideNavigationTypes";
import { IconChevronDown, IconChevronUp, IconUser } from "../../icons";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { PropsWithChildren, useState } from "react";
import { useSideNavigationCollapseContext } from "../side-navigation-contexts/SideNavigationCollapseContext";
import { useDebounce } from "../../../helpers/debounce/debounce";
import classNames from "classnames";
import { useSideNavigationItem } from "../side-navigation-contexts/SideNavigationItemContext";

export const SideNavigationProfile = ({
  name,
  subHeading,
  items,
}: SideNavigationProfileProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { isCollapsed } = useSideNavigationCollapseContext();

  const debounceLeave = useDebounce(() => {
    setIsOpen(false);
  }, 200);

  const handleOpen = () => {
    setIsOpen((prevValue) => !prevValue);
  };

  const handleMouseEnter = () => {
    isCollapsed && setIsOpen(true);
    debounceLeave.cancel();
  };

  const handleMouseLeave = () => {
    isCollapsed && debounceLeave.debounce();
  };

  const buttonStyling = classNames(
    styles.sideNavigationProfileContainer,
    {
      [styles.opened]: isOpen,
      [styles.collapsed]: isCollapsed,
    },
    styles.unset,
  );

  const contentItems = items.map((item, itemIndex) => {
    return (
      <DropdownMenu.Item
        autoFocus
        className={styles.profileItem}
        key={itemIndex}
        onClick={() => {
          item.onClick();
          setIsOpen(false);
        }}
      >
        {item.Icon && <item.Icon />}
        <span className={styles.profileItemText}>{item.label}</span>
      </DropdownMenu.Item>
    );
  });

  return (
    <DropdownMenu.Root modal={false} open={isOpen} onOpenChange={handleOpen}>
      <DropdownMenu.Trigger asChild>
        <button
          tabIndex={0}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className={buttonStyling}
        >
          <UserInfo
            collapsed={isCollapsed}
            open={isOpen}
            name={name}
            subHeading={subHeading}
          />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          onFocusOutside={(e) => e.preventDefault()}
          loop
          align={"end"}
          side={"right"}
          sideOffset={4}
          className={styles.profileItemContainer}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <ProfileContent
            collapsed={isCollapsed}
            name={name}
            subHeading={subHeading}
          >
            {contentItems}
          </ProfileContent>
          <DropdownMenu.Arrow className={styles.dropdownMenuArrow} />
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

type UserInfoProps = {
  collapsed: boolean;
  open: boolean;
  name: string;
  subHeading?: string;
};

const UserInfo = (props: UserInfoProps) => {
  const userIcon = (
    <IconUser aria-label={"User avatar icon"} className={styles.avatarImage} />
  );

  if (props.collapsed) {
    return userIcon;
  }

  const chevron = props.open ? <IconChevronUp /> : <IconChevronDown />;

  return (
    <>
      <span className={styles.imageAndInfo}>
        {userIcon}
        <span className={styles.userInfo}>
          <span className={styles.displayName}>{props.name}</span>
          <span className={styles.displaySubHeading}>{props.subHeading}</span>
        </span>
      </span>
      {chevron}
    </>
  );
};

type ProfileContentProps = {
  collapsed: boolean;
  name: string;
  subHeading?: string;
};

const ProfileContent = (props: PropsWithChildren<ProfileContentProps>) => {
  if (!props.collapsed) {
    // Just show menu items
    return props.children;
  }

  // Shows the profile info as well as any menu items
  return (
    <>
      <span className={styles.collapsedProfileInfo}>
        <span className={styles.collapsedProfileInfoText}>
          <span className={styles.collapsedProfileInfoTextName}>
            {props.name}
          </span>
          <span className={styles.collapsedProfileInfoTextSubHeading}>
            {props.subHeading}
          </span>
        </span>
      </span>
      {props.children}
    </>
  );
};
